// TenantContext.js
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '../../api';

const TenantContext = createContext();

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
};

export const TenantProvider = ({ children }) => {
  // Initialize from localStorage if available
  const getInitialData = () => {
    try {
      const stored = localStorage.getItem('tenantData');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error('Error reading tenant data from localStorage:', e);
    }
    return {
      institutionId: null,
      profileName: 'Admin',
      institutionName: 'Truyon',
      logoUrl: null,
      userDetails: null,
      organizationExists: false
    };
  };

  const [tenantData, setTenantData] = useState(getInitialData);
  const [loading, setLoading] = useState(false);

  // Save to localStorage whenever data changes
  useEffect(() => {
    try {
      localStorage.setItem('tenantData', JSON.stringify(tenantData));
    } catch (e) {
      console.error('Error saving tenant data to localStorage:', e);
    }
  }, [tenantData]);

  const fetchTenantData = useCallback(async (userId, tenantId) => {
    if (!userId || !tenantId) {
      return;
    }
    
    // Check if we already have data for this tenant
    if (tenantData.institutionId === tenantId && tenantData.institutionName !== 'Truyon') {
      console.log('✅ Using cached tenant data');
      return;
    }
    
    try {
      setLoading(true);
      
      const [userResponse, organizationResponse, basicInfoResponse] = await Promise.all([
        api.get(`/api/users/${userId}`),
        api.get(`/api/organization/${tenantId}`).catch(() => null),
        api.get(`/api/organization/basic-info/${tenantId}`).catch(() => null)
      ]);
      
      let institutionName = null;
      let institutionLogo = null;
      let profileName = 'Admin';
      let organizationExists = false;
      
      // Get user details
      if (userResponse?.data) {
        profileName = userResponse.data.name || userResponse.data.firstName || 'Admin';
      }
      
      // Get organization exists
      if (organizationResponse?.data) {
        organizationExists = organizationResponse.data.exists || false;
      }
      
      // Get institution name from basic info
      if (basicInfoResponse?.data) {
        const basicInfo = basicInfoResponse.data;
        institutionName = basicInfo.name || basicInfo.organizationName || basicInfo.orgName;
        institutionLogo = basicInfo.logoUrl || basicInfo.logo || basicInfo.logo_url;
      }
      
      // If no name from basic info, try organization response
      if (!institutionName && organizationResponse?.data) {
        const orgData = organizationResponse.data;
        institutionName = orgData.name || orgData.organizationName || orgData.orgName;
        institutionLogo = orgData.logoUrl || orgData.logo || orgData.logo_url;
      }
      
      // If still no name, try user response
      if (!institutionName && userResponse?.data) {
        const userData = userResponse.data;
        institutionName = userData.institutionName || 
                         userData.institution?.name ||
                         userData.organization?.name;
        institutionLogo = userData.institutionLogo || 
                         userData.institution?.logoUrl ||
                         userData.organization?.logoUrl;
      }
      
      const newData = {
        institutionId: tenantId,
        profileName: profileName || 'Admin',
        institutionName: institutionName || 'Truyon',
        logoUrl: institutionLogo || null,
        userDetails: userResponse?.data || null,
        organizationExists: organizationExists
      };
      
      setTenantData(newData);
      localStorage.setItem('tenantData', JSON.stringify(newData));
      
    } catch (err) {
      console.error('Error fetching tenant data:', err);
    } finally {
      setLoading(false);
    }
  }, [tenantData.institutionId, tenantData.institutionName]);

  const updateTenantData = useCallback((data) => {
    setTenantData(prev => {
      const newData = { ...prev, ...data };
      localStorage.setItem('tenantData', JSON.stringify(newData));
      return newData;
    });
  }, []);

  // Clear tenant data on logout
  const clearTenantData = useCallback(() => {
    localStorage.removeItem('tenantData');
    setTenantData({
      institutionId: null,
      profileName: 'Admin',
      institutionName: 'Truyon',
      logoUrl: null,
      userDetails: null,
      organizationExists: false
    });
  }, []);

  return (
    <TenantContext.Provider value={{ 
      tenantData, 
      loading, 
      fetchTenantData, 
      updateTenantData,
      clearTenantData
    }}>
      {children}
    </TenantContext.Provider>
  );
};